import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import Button from "../../shared/ui/Button";
import "./HomePage.scss";
import Container from "../../shared/ui/Container";
import heroImage from "./hero_image.png";
import featuresImage from "./feature.png";

const HomePage = () => {
  const [visibleCards, setVisibleCards] = useState([]);
  const cardRefs = useRef([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const cardIndex = parseInt(entry.target.dataset.cardIndex);
            setVisibleCards((prev) => [...prev, cardIndex]);
          }
        });
      },
      { threshold: 0.1 }
    );

    cardRefs.current.forEach((ref) => {
      if (ref) observer.observe(ref);
    });

    return () => observer.disconnect();
  }, []);

  return (
    <div className="home-page">
      <section className="hero" aria-labelledby="hero-title">
        <Container className="hero__container">
          <div className="hero__content animate-fade-in-left">
            <div className="hero__badge">+ Introducing Digital Access</div>
            <h1 id="hero-title" className="hero__title">
              From barriers to bridges —
            </h1>
            <p className="hero__subtitle">
              accessibility audits for Canadian businesses
            </p>
            <p className="hero__description">
              Our mission is simple: make digital experiences usable for
              everyone. We combine manual testing, assistive technology reviews,
              and WCAG expertise to help your website meet the highest
              accessibility standards in Canada.
            </p>
            <div className="hero__actions">
              <Link to="/what-we-do">
                <Button variant="primary">Learn More</Button>
              </Link>
              <Link to="/contact">
                <Button variant="secondary">Get a free consultation</Button>
              </Link>
            </div>
          </div>
          <div className="hero__visual animate-fade-in-right">
            <div className="hero__image">
              <img
                src={heroImage}
                alt="Digital accessibility consulting - making websites accessible for everyone"
                className="hero__image-content"
              />
            </div>
          </div>
        </Container>
      </section>

      <section
        className="accessibility-matters"
        aria-labelledby="accessibility-title"
      >
        <Container>
          <div className="accessibility-matters__container">
            <div className="accessibility-matters__content">
              <h2
                id="accessibility-title"
                className="accessibility-matters__title"
              >
                Why Accessibility Matters
              </h2>
              <p className="accessibility-matters__description">
                Accessibility isn't just about ethics — it's smart business.
                Here's how making your digital products accessible benefits your
                company:
              </p>
            </div>

            <div className="accessibility-matters__cards">
              <article className="accessibility-card">
                <div className="accessibility-card__icon accessibility-card__icon--users">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#60a5fa"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    class="lucide lucide-user-round-plus-icon lucide-user-round-plus"
                  >
                    <path d="M2 21a8 8 0 0 1 13.292-6" />
                    <circle cx="10" cy="8" r="5" />
                    <path d="M19 16v6" />
                    <path d="M22 19h-6" />
                  </svg>
                </div>
                <h3 className="accessibility-card__title">Reach More Users</h3>
                <p className="accessibility-card__description">
                  1 in 5 Canadians has a disability. Making your website, apps,
                  and PDFs accessible opens your digital presence to a larger
                  audience.
                </p>
                <div className="accessibility-card__divider"></div>
              </article>

              <article className="accessibility-card">
                <div className="accessibility-card__icon accessibility-card__icon--chart">
                  <svg viewBox="0 0 24 24" fill="none">
                    <path
                      d="M12 20V10M8 20V14M16 20V6M3 20H21"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                  </svg>
                </div>
                <h3 className="accessibility-card__title">
                  Increase Conversions
                </h3>
                <p className="accessibility-card__description">
                  Accessible websites are better structured and easier for
                  everyone to use — studies show improved UX can increase
                  conversions by 10–15%.
                </p>
                <div className="accessibility-card__divider"></div>
              </article>

              <article className="accessibility-card">
                <div className="accessibility-card__icon accessibility-card__icon--shield">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#60a5fa"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    class="lucide lucide-monitor-check-icon lucide-monitor-check"
                  >
                    <path d="m9 10 2 2 4-4" />
                    <rect width="20" height="14" x="2" y="3" rx="2" />
                    <path d="M12 17v4" />
                    <path d="M8 21h8" />
                  </svg>
                </div>
                <h3 className="accessibility-card__title">
                  Stay Legally Compliant
                </h3>
                <p className="accessibility-card__description">
                  Comply with AODA, WCAG, and ADA standards to avoid legal risks
                  and potential fines while demonstrating corporate
                  responsibility.
                </p>
                <div className="accessibility-card__divider"></div>
              </article>

              <article className="accessibility-card">
                <div className="accessibility-card__icon accessibility-card__icon--star">
                  <svg viewBox="0 0 24 24" fill="none">
                    <polygon
                      points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                <h3 className="accessibility-card__title">
                  Enhance Brand Reputation
                </h3>
                <p className="accessibility-card__description">
                  Companies that invest in accessibility see higher customer
                  satisfaction and repeat visits. Show your commitment to
                  inclusion and social responsibility.
                </p>
                <div className="accessibility-card__divider"></div>
              </article>
            </div>
          </div>
        </Container>
      </section>

      <section className="features" aria-labelledby="features-title">
        <Container>
          <h2 id="features-title" className="features__title">
            Here’s why our clients trust us:
          </h2>
          <div className="features__container">
            <div className="features__cards">
              <article
                ref={(el) => (cardRefs.current[0] = el)}
                data-card-index="0"
                className={`feature-card ${
                  visibleCards.includes(0)
                    ? "scroll-animate-in"
                    : "scroll-hidden"
                }`}
              >
                <h3 className="feature-card__title">Certified Experts</h3>
                <p className="feature-card__description">
                  Trained in WCAG, AODA, and ADA standards, our team conducts
                  testing on real devices with accessibility experts, ensuring
                  your product works for everyone.
                </p>
              </article>
              <article
                ref={(el) => (cardRefs.current[1] = el)}
                data-card-index="1"
                className={`feature-card ${
                  visibleCards.includes(1)
                    ? "scroll-animate-in"
                    : "scroll-hidden"
                }`}
              >
                <h3 className="feature-card__title">Real-User Insights</h3>
                <p className="feature-card__description">
                  Testing with people using assistive technologies to uncover
                  barriers that automated tools might miss.
                </p>
              </article>
              <article
                ref={(el) => (cardRefs.current[2] = el)}
                data-card-index="2"
                className={`feature-card ${
                  visibleCards.includes(2)
                    ? "scroll-animate-in"
                    : "scroll-hidden"
                }`}
              >
                <h3 className="feature-card__title">Actionable Reports</h3>
                <p className="feature-card__description">
                  We provide clear, step-by-step recommendations so your team
                  can efficiently improve accessibility and compliance.
                </p>
              </article>
            </div>
            <img
              src={featuresImage}
              alt="Features"
              className="features__image"
            />
          </div>
        </Container>
      </section>

      <section className="cta" aria-labelledby="cta-title">
        <Container>
          <h2 id="cta-title" className="cta__title">
            Make your digital presence accessible to everyone
          </h2>

          <div className="cta__content">
            <h3 className="cta__description">
              Accessibility isn’t just the right thing to do — it’s also the
              law. We’ll guide you through every step of your compliance journey
              under the Accessibility Canada Act.
            </h3>
            <div className="cta__border">
              <ul className="cta__list">
                <li className="feature-card feature-card--mobile">
                  Only <strong>1% of websites</strong> are fully accessible
                  today. Yet <strong>1 in 4 people</strong> lives with a
                  disability — that’s 25% of your potential users.
                </li>
                <li className="feature-card feature-card--mobile">
                  Research shows that <strong>70%</strong> of people with
                  disabilities never return to websites that are not accessible.
                </li>{" "}
                <li className="feature-card feature-card--mobile">
                  Our clients see a{" "}
                  <strong>14–20% increase in user engagement</strong> after
                  fixing accessibility issues — improving both inclusivity and
                  business performance.
                </li>
              </ul>

              <Link to="/contact">
                <Button variant="primary" ariaLabel="Contact us to get started">
                  Start Your Accessibility Journey
                </Button>
              </Link>
            </div>
          </div>
        </Container>
      </section>
    </div>
  );
};

export default HomePage;
